// One-click toggle that pins / unpins an entity in the operator's auto-
// created `my-tracked` watchlist. Renders into ObjectHeader.actions on
// vendor / agency / opportunity detail pages.
//
// Hassle-free: no naming a list, no dropdown. Power users can still
// organise into named lists via /v1/watchlists/* (separate UI). The brief
// feed on /terminal reads from this list (and others) for the
// since-you-last-looked strip.
//
// Optimistic update — flips immediately on click; rolls back on server
// error. The /me/relations query is the source of truth on mount.

import { apiFetch } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

type TrackableEntityType = 'tender' | 'award' | 'legal_entity' | 'agency' | 'standing_offer';

interface RelationsResponse {
  tracked: boolean;
  in_pipeline: boolean;
}

interface TrackButtonProps {
  entityType: TrackableEntityType;
  entityId: string;
}

function relationsKey(entityType: string, entityId: string): readonly unknown[] {
  return ['me', 'relations', entityType, entityId] as const;
}

export function TrackButton({ entityType, entityId }: TrackButtonProps) {
  const qc = useQueryClient();
  const key = relationsKey(entityType, entityId);

  const rel = useQuery({
    queryKey: key,
    enabled: !!entityId,
    queryFn: () =>
      apiFetch<RelationsResponse>(
        `/v1/me/relations?entity_type=${encodeURIComponent(entityType)}&entity_id=${encodeURIComponent(entityId)}`,
      ),
    staleTime: 30_000,
  });

  const tracked = rel.data?.tracked ?? false;

  const mutate = useMutation({
    mutationFn: async (next: boolean) => {
      // apiFetch JSON.stringifies the body itself + sets content-type.
      // Pre-stringifying double-encoded the payload → server saw a JSON
      // string, not an object → Zod rejected with validation_failed (400).
      await apiFetch('/v1/me/track', {
        method: next ? 'POST' : 'DELETE',
        body: { entity_type: entityType, entity_id: entityId },
      });
    },
    onMutate: async (next) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<RelationsResponse>(key);
      qc.setQueryData<RelationsResponse>(key, {
        tracked: next,
        in_pipeline: prev?.in_pipeline ?? false,
      });
      return { prev };
    },
    onError: (_err, _next, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: ['brief'] });
    },
  });

  if (rel.isLoading) {
    return (
      <button type="button" className="btn xs gh" disabled>
        TRACK
      </button>
    );
  }

  return (
    <button
      type="button"
      className={tracked ? 'btn xs primary' : 'btn xs gh'}
      onClick={() => mutate.mutate(!tracked)}
      disabled={mutate.isPending}
      aria-pressed={tracked}
      title={tracked ? 'Untrack — remove from My Tracked' : 'Track — surface changes in My Brief'}
    >
      {tracked ? 'TRACKED' : 'TRACK'}
    </button>
  );
}
